import React, { CSSProperties, useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { useModalBehavior } from '@chegg-ui/modal/'
import {
  userClick,
  emitComponentViewEvent,
} from '@chegg-wt/core/state/instrumentation/actions'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import {
  modalTypeSelector,
  modalDataSelector,
} from '@chegg-wt/core/state/modals/selectors'
import { navigated } from '@chegg-wt/core/state/app/actions'

import { Confetti } from '@chegg-ui/confetti'

import {
  ComponentPayload,
  componentOrigins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import {
  UserClickPayload,
  origins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import Modal from '..'
import { ModalContent } from '../styled'
import * as Styled from './styled'
import { CLAIM_OFFER_NOW, getBodyText, getHeaderText } from './constants'
import { SaleBadge } from './SaleBadge'

const DISCOUNT_MODAL_ID = 'discount-modal'

export interface Props {
  closeModal: (name: string) => void
  modalName: string
  modalData: {
    price: string
    basePrice: number
    discountPercentage: number
  }
  emitComponentViewEvent: (payload: ComponentPayload) => void
  userClick: (payload: UserClickPayload) => void
}

export const DiscountModal: React.FunctionComponent<Props> = ({
  closeModal,
  modalName,
  modalData,
  emitComponentViewEvent,
  userClick,
}) => {
  const { price, basePrice, discountPercentage } = modalData
  const { toUpgrade } = useRoutesContext()
  const dispatch = useDispatch()

  useEffect(() => {
    emitComponentViewEvent({
      origin: componentOrigins.priceExperimentModal,
      text: getHeaderText(discountPercentage),
    })
  }, [])

  const onClose = () => {
    closeModal(modalName)
  }

  const { modalRef, onModalKeyDown } = useModalBehavior({
    isOpen: true,
    onClose,
    id: DISCOUNT_MODAL_ID,
  })

  React.useEffect(() => {
    modalRef.current?.focus()
  }, [modalRef])

  const onClickCheckPaper = () => {
    userClick({ origin: origins.priceModalClaimOfferClick, text: CLAIM_OFFER_NOW })
    dispatch(navigated(toUpgrade()))
  }

  const confettiCustomCSS: CSSProperties = {
    zIndex: 10000,
  }

  return (
    discountPercentage && (
      <>
        <Modal
          ref={modalRef}
          onKeyDown={onModalKeyDown}
          closeButton
          enableScroll={false}
          outsideClick
          showModal
          showBackdrop
          fullWidth={false}
          id={DISCOUNT_MODAL_ID}
          maxWidth="552px"
        >
          <Styled.ConfettiWrapper>
            <Confetti customCSS={confettiCustomCSS} />
          </Styled.ConfettiWrapper>
          <ModalContent fullMobileModal={false}>
            <Styled.BadgeWrapperDesktop>
              <SaleBadge />
            </Styled.BadgeWrapperDesktop>
            <Styled.AlignCenter>
              <Styled.BadgeWrapperMobile>
                <SaleBadge />
              </Styled.BadgeWrapperMobile>
              <Styled.BoldHeader>{getHeaderText(discountPercentage)}</Styled.BoldHeader>
              <Styled.BodyText
                dangerouslySetInnerHTML={{
                  __html: getBodyText(price, basePrice),
                }}
              />
              <Styled.ButtonContainer>
                <Styled.Button
                  data-test="check-paper-btn"
                  onClick={onClickCheckPaper}
                  size="large"
                >
                  {CLAIM_OFFER_NOW}
                </Styled.Button>
              </Styled.ButtonContainer>
            </Styled.AlignCenter>
          </ModalContent>
        </Modal>
      </>
    )
  )
}

const mapStateToProps = (state) => ({
  modalData: modalDataSelector(state),
  modalName: modalTypeSelector(state),
})

const mapDispatchToProps = {
  closeModal,
  userClick,
  emitComponentViewEvent,
}

export default connect(mapStateToProps, mapDispatchToProps)(DiscountModal)
